import { FC, useMemo } from 'react';
import Location from '~/icons/location.svg';
import LocationActive from '~/icons/location-active.svg';
import Spinner from '~/shared/components/Spinner/Spinner';
import { StoreAvailabilitySearchInput } from './StoreAvailabilitySearchInput';
import { ResetButton } from '~/shared/components/Button/ResetButton';
import SwitchElement from '~/shared/components/form/components/controllable-elements/switch-element';
import { VirtualizedList } from '~/features/productList/components/VirtualizedList';
import { RefinementListRow } from '~/features/productList/components/RefinementListRow';
import RefinementEmptyList from '~/features/productList/components/FilterList/components/RefinementEmptyList';
import { StickyFilterHeader } from '~/features/productList/components/StickyFilterHeader';
import { CountBadge } from '~/features/productList/components/CountBadge';
import CloseThinIcon from '~/icons/close-thin.svg';
import filterSelectionStyles from '~/features/productList/components/FilterSelectionHeader/FilterSelectionHeader.module.scss';
import { useTranslation } from '~/shared/utils/translation';
import { Facets } from '~/services/instantsearch';
import { useStoreAvailability } from '~/features/productList/hooks/useStoreAvailability';
import { useStoreAvailabilityList } from '~/features/productList/hooks/useStoreAvailabilityList';
import { useStoreAvailabilitySearch } from '~/features/productList/hooks/useStoreAvailabilitySearch';
import { usePinnedRefinementItems } from '~/features/productList/hooks/usePinnedRefinementItems';
import { useDrawerFilterTracking } from '~/features/productList/hooks/useDrawerFilterTracking';
import styles from './StoreAvailabilityPanel.module.scss';
import { useScrollToTopOnSearch } from '~/features/productList/hooks/useScrollToTopOnSearch';

type StoreAvailabilityPanelProps = {
    // False while this facet's detail view is mounted but hidden (see FilterAttributePanel).
    isActive: boolean;
};

// Mobile equivalent of StoreAvailabilityToggles + StoreAvailabilityDrawer, combined into one panel.
export const StoreAvailabilityPanel: FC<StoreAvailabilityPanelProps> = ({ isActive }) => {
    const { translate } = useTranslation();
    const { trackFilter } = useDrawerFilterTracking();

    const {
        isOnlineEnabled,
        isStoreEnabled,
        selectedStores,
        storeItems,
        onlineCount,
        anyStoreCount,
        setIsOnlineEnabled,
        setIsStoreEnabled,
        toggleStore,
        clearStores,
    } = useStoreAvailability();
    const selectedStoreCount = selectedStores.length;

    const { allStores, isLoading: isLoadingStores } = useStoreAvailabilityList({
        enabled: isActive,
    });

    const {
        searchQuery,
        setSearchQuery,
        storeSearchInputRef,
        nearbyStores,
        searchError,
        toggleGeoLocation,
        isGeoActive,
        isLocating,
    } = useStoreAvailabilitySearch(allStores);

    useScrollToTopOnSearch(searchQuery);

    const items = useMemo(
        () =>
            (nearbyStores ?? []).map((store) => {
                const algoliaItem = storeItems.find((item) => item.value === store.name);

                return {
                    value: store.name,
                    label: store.name,
                    count: algoliaItem?.count ?? 0,
                    isRefined: selectedStores.includes(store.name),
                    isDisabled: !algoliaItem,
                };
            }),
        [nearbyStores, storeItems, selectedStores],
    );

    const pinnedItems = usePinnedRefinementItems(items, searchQuery, isActive && !isLoadingStores);

    const handleRefine = (value: string) => {
        if (!value) return;

        trackFilter(Facets.STORE, value, {
            isRefinement: false,
            isRefined: selectedStores.includes(value),
        });
        toggleStore(value);
    };

    const handleOnlineChange = (enabled: boolean) => {
        trackFilter(Facets.STORE_WEB, 'in_stock_web', { isRefinement: false, isRefined: !enabled });
        setIsOnlineEnabled(enabled);
    };

    const handleStoreChange = (enabled: boolean) => {
        trackFilter(Facets.STORE, 'in_stock_store', { isRefinement: false, isRefined: !enabled });
        setIsStoreEnabled(enabled);
    };

    return (
        <div className={styles.panel}>
            <StickyFilterHeader>
                <div className={styles.toggleRow}>
                    <SwitchElement
                        checked={isOnlineEnabled}
                        onChange={(event) => handleOnlineChange(event.target.checked)}
                        label={translate('plp.storeAvailability.online')}
                        helpText={
                            onlineCount
                                ? translate('plp.facetSelection.resultCount', {
                                      count: `${onlineCount}`,
                                  })
                                : undefined
                        }
                    />
                </div>

                <div className={styles.toggleRow}>
                    <SwitchElement
                        checked={isStoreEnabled}
                        onChange={(event) => handleStoreChange(event.target.checked)}
                        label={translate('plp.storeAvailability.selectStore')}
                        helpText={
                            anyStoreCount
                                ? translate('plp.facetSelection.resultCount', {
                                      count: `${anyStoreCount}`,
                                  })
                                : undefined
                        }
                        chip={
                            !isStoreEnabled ? undefined : selectedStoreCount > 0 ? (
                                <>
                                    <CountBadge count={selectedStoreCount} />
                                    <ResetButton
                                        className={filterSelectionStyles.selectionHeaderClearChip}
                                        aria-label={translate('plp.facetSelection.clear')}
                                        onClick={clearStores}
                                    >
                                        {translate('plp.facetSelection.clear')}
                                        <CloseThinIcon />
                                    </ResetButton>
                                </>
                            ) : (
                                // Placeholder text pending a real translation key (deferred for now).
                                <span className={styles.allStoresChip}>(Alle butikker)</span>
                            )
                        }
                    />
                </div>

                {/* Static "Vælg butik" heading per Figma — badge/clear chip live on the toggle row above instead. */}
                {isStoreEnabled && (
                    <div className={styles.selectStoreHeading}>
                        {translate('plp.storeAvailability.selectStoreLink')}
                    </div>
                )}

                {isStoreEnabled && (
                    <div className={styles.searchRow}>
                        <StoreAvailabilitySearchInput
                            inputRef={storeSearchInputRef}
                            value={searchQuery}
                            setValue={setSearchQuery}
                            className={styles.searchInput}
                        />
                        <ResetButton
                            aria-label={translate('findStore.closeToMyLocation')}
                            aria-pressed={isGeoActive}
                            aria-busy={isLocating}
                            className={styles.locationButton}
                            disabled={isLocating}
                            onClick={toggleGeoLocation}
                        >
                            {isLocating ? (
                                <Spinner />
                            ) : isGeoActive ? (
                                <LocationActive />
                            ) : (
                                <Location />
                            )}
                        </ResetButton>
                    </div>
                )}

                {isStoreEnabled && searchError && (
                    <div className={styles.searchError}>{searchError}</div>
                )}
            </StickyFilterHeader>

            {isStoreEnabled &&
                (isLoadingStores ? (
                    <div className={styles.loadingContainer}>
                        <Spinner />
                    </div>
                ) : pinnedItems.length === 0 ? (
                    <RefinementEmptyList />
                ) : (
                    <VirtualizedList
                        isActive={isActive}
                        items={pinnedItems}
                        itemKey={(item) => item.value}
                        renderItem={(item) => (
                            <RefinementListRow
                                label={item.label}
                                value={item.value}
                                count={item.count}
                                isRefined={item.isRefined}
                                isDisabled={item.isDisabled}
                                shouldShowCount
                                onRefine={handleRefine}
                            />
                        )}
                    />
                ))}
        </div>
    );
};

export default StoreAvailabilityPanel;
